// src/components/TeacherLocations.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaLocationDot } from "react-icons/fa6";
import apiKey from "../config/apiKey";
import Modal from "./Modal";

const BASE = `${apiKey}/api/edolutions/teacher/locations`; // adjust if namespaced

export default function TeacherLocations({ token }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // add modal
  const [addOpen, setAddOpen] = useState(false);
  const [newText, setNewText] = useState("");
  const [newPlaceId, setNewPlaceId] = useState("");
  const [newPrimary, setNewPrimary] = useState(false);
  const addRef = useRef(null);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editPlaceId, setEditPlaceId] = useState("");
  const editRef = useRef(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  // Bind Google Autocomplete helper
  const bindAutocomplete = useCallback((inputEl, onChange) => {
    if (!inputEl || !window.google?.maps?.places) return;
    const ac = new window.google.maps.places.Autocomplete(inputEl, {
      componentRestrictions: { country: "pk" },
      fields: ["formatted_address", "place_id"],
    });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      onChange(place);
    });
  }, []);

  useEffect(() => {
    if (addOpen && addRef.current) {
      bindAutocomplete(addRef.current, (place) => {
        setNewText(place?.formatted_address || "");
        setNewPlaceId(place?.place_id || "");
      });
    }
  }, [addOpen, bindAutocomplete]);

  useEffect(() => {
    if (editOpen && editRef.current) {
      bindAutocomplete(editRef.current, (place) => {
        setEditText(place?.formatted_address || "");
        setEditPlaceId(place?.place_id || "");
      });
    }
  }, [editOpen, bindAutocomplete]);

  // API
  const fetchLocations = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(BASE, { headers: authHeader });
      setLocations(data || []);
    } catch {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const addLocation = async (e) => {
    e?.preventDefault();
    if (!newText.trim()) return toast.info("Pick a location first");
    try {
      await axios.post(
        BASE,
        {
          location_text: newText.trim(),
          google_place_id: newPlaceId || null,
          is_primary: newPrimary,
        },
        { headers: authHeader }
      );
      toast.success("Location added");
      setAddOpen(false);
      setNewText("");
      setNewPlaceId("");
      setNewPrimary(false);
      fetchLocations();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Unable to add location");
    }
  };

  const openEdit = (loc) => {
    setEditId(loc.id);
    setEditText(loc.location_text);
    setEditPlaceId(loc.google_place_id || "");
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e?.preventDefault();
    if (!editText.trim()) return toast.info("Location text required");
    try {
      const { data } = await axios.put(
        `${BASE}/${editId}`,
        {
          location_text: editText.trim(),
          google_place_id: editPlaceId || null,
        },
        { headers: authHeader }
      );
      toast.success("Updated");
      setEditOpen(false);
      setEditId(null);
      setEditText("");
      setEditPlaceId("");
      // Update locally
      setLocations((prev) => prev.map((l) => (l.id === data.data.id ? data.data : l)));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Update failed");
    }
  };

  const removeLocation = async (id) => {
    try {
      await axios.delete(`${BASE}/${id}`, { headers: authHeader });
      setLocations((prev) => prev.filter((x) => x.id !== id));
      toast.success("Location deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const makePrimary = async (id) => {
    try {
      await axios.post(`${BASE}/${id}/primary`, null, { headers: authHeader });
      toast.success("Primary set");
      setLocations((prev) => prev.map((l) => ({ ...l, is_primary: l.id === id ? 1 : 0 })));
    } catch {
      toast.error("Could not set primary");
    }
  };

  return (
    <div className="w-full">
      <div className="mt-8 flex items-center justify-between">
        <div>
          <p className="text-lg font-semibold">Teaching Locations</p>
          <p className="text-xs text-gray-500">
            Add areas where you’re available to teach. One can be marked as primary.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="text-xs bg-[#4FD1C5] text-white px-4 py-2 rounded-lg"
        >
          + Add Location
        </button>
      </div>

      {/* List */}
      <div className="mt-4">
        {loading ? (
          <div className="w-full py-8 text-center text-sm text-gray-500">Loading locations…</div>
        ) : locations.length === 0 ? (
          <div className="w-full py-6 text-center text-sm text-gray-500">No locations added yet.</div>
        ) : (
          <ul className="space-y-2">
            {locations.map((loc) => (
              <li
                key={loc.id}
                className="border bg-white rounded-xl p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-full p-2 bg-[#F9FBFF]">
                    <FaLocationDot className="opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{loc.location_text}</p>
                    <p className="text-[10px] text-gray-500">
                      {loc.google_place_id || "No place_id"}
                    </p>
                  </div>
                  {/* {Number(loc.is_primary) === 1 && (
                    <span className="ml-2 text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                      Primary
                    </span>
                  )} */}
                </div>

                <div className="flex items-center gap-2 ml-3">
                  {/* {Number(loc.is_primary) !== 1 && (
                    <button
                      type="button"
                      onClick={() => makePrimary(loc.id)}
                      className="text-xs border border-[#4FD1C5] text-[#4FD1C5] px-3 py-1.5 rounded-lg"
                    >
                      Make Primary
                    </button>
                  )} */}
                  <button
                    type="button"
                    onClick={() => openEdit(loc)}
                    className="text-xs bg-slate-100 px-3 py-1.5 rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => removeLocation(loc.id)}
                    className="text-xs bg-rose-500 text-white px-3 py-1.5 rounded-lg"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add Modal */}
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Teaching Location"
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAddOpen(false)} className="px-4 py-2 rounded bg-gray-100">
              Cancel
            </button>
            <button type="button" onClick={addLocation} className="px-4 py-2 rounded bg-[#4FD1C5] text-white">
              Save
            </button>
          </div>
        }
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <label className="text-xs text-gray-500">Location</label>
          <div className="relative mt-1">
            <input
              ref={addRef}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              className="outline-none px-3 py-2 w-full bg-white rounded-lg border"
              placeholder="Search & select a location (Google)"
              type="text"
            />
            <FaLocationDot className="absolute right-3 top-1/2 -translate-y-1/2 opacity-40" />
          </div>
          <div className="flex items-center gap-2 mt-3">
            <input
              id="newPrimary"
              type="checkbox"
              className="rounded"
              checked={newPrimary}
              onChange={(e) => setNewPrimary(e.target.checked)}
            />
            <label htmlFor="newPrimary" className="text-xs">
              Set as primary
            </label>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Location"
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditOpen(false)} className="px-4 py-2 rounded bg-gray-100">
              Cancel
            </button>
            <button type="button" onClick={saveEdit} className="px-4 py-2 rounded bg-emerald-600 text-white">
              Update
            </button>
          </div>
        }
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <label className="text-xs text-gray-500">Location</label>
          <div className="relative mt-1">
            <input
              ref={editRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="outline-none px-3 py-2 w-full bg-white rounded-lg border"
              placeholder="Edit location"
              type="text"
            />
            <FaLocationDot className="absolute right-3 top-1/2 -translate-y-1/2 opacity-40" />
          </div>
          <label className="text-xs text-gray-500 mt-3 block">Google Place ID (optional)</label>
          <input
            value={editPlaceId}
            onChange={(e) => setEditPlaceId(e.target.value)}
            className="outline-none px-3 py-2 w-full bg-white rounded-lg border mt-1"
            placeholder="place_id"
            type="text"
          />
        </form>
      </Modal>
    </div>
  );
}
